// The plan of record. `docs/business/05_2026-08-15_gtm_prescription.md` §2.2
// is the ONLY source for these numbers — `docs/GTM.md`'s pricing section is
// explicitly superseded and must never be read instead (CLAUDE.md, PRD 18).
//
// `GET /licenses` returns a bare plan string (`"pro"`, `"scale"`, ...) with
// no price attached — the Wave 1 handoff says so outright: "if the billing
// screen needs a dollar figure per plan it has to come from a static
// pricing table in the frontend ... not this API." This is that table.
//
// Every figure below was checked line by line against §2.2 while building
// this screen. They match exactly; nothing here is rounded or invented.

export type PlanId = "trial" | "pro" | "scale" | "enterprise";

export interface PlanInfo {
  id: PlanId;
  label: string;
  /** §2.2 "Price" column. */
  price: string;
  /** §2.2's annual alternative, where the tier has one. */
  cadenceNote?: string;
  /** §2.2 "Domains" column, rendered as prose. */
  domains: string;
  /** §2.2 "Domains" column as a number, for the domains-bound meter.
   *  `null` means unlimited (Enterprise) — the meter is skipped, not drawn
   *  as a fraction of nothing. */
  domainLimit: number | null;
  /** §2.2 "Pageview ceiling" column, rendered as prose. */
  pageviewCeiling: string;
  /** One line from §2.2 "What they get". */
  headline: string;
}

export const PLAN_INFO: Record<PlanId, PlanInfo> = {
  trial: {
    id: "trial",
    label: "Trial",
    price: "$0 / 14 days",
    domains: "1 domain",
    domainLimit: 1,
    pageviewCeiling: "100k pageviews",
    headline: "Full Pro, no signup — badge shown, auto-expires.",
  },
  pro: {
    id: "pro",
    label: "Pro",
    price: "$149/mo",
    cadenceNote: "or $1,490/yr (2 months free)",
    domains: "1 domain",
    domainLimit: 1,
    pageviewCeiling: "250k pageviews/mo soft",
    headline: "Embed widget · reader analytics · monthly report email.",
  },
  scale: {
    id: "scale",
    label: "Scale",
    price: "$449/mo",
    cadenceNote: "or $4,490/yr",
    domains: "3 domains",
    domainLimit: 3,
    pageviewCeiling: "1M pageviews/mo soft",
    headline: "Everything in Pro · CSV/API export · priority support.",
  },
  enterprise: {
    id: "enterprise",
    label: "Enterprise",
    price: "from $25k/yr",
    domains: "Unlimited domains",
    domainLimit: null,
    pageviewCeiling: "Custom",
    headline: "Everything in Scale · self-hosted option · SSO · signed DPA.",
  },
};

/** The three purchasable tiers drawn as cards on frame 19. Trial is a
 *  status a license can be in, not something a signed-in org buys, so it
 *  is deliberately excluded from this list. */
export const UPGRADE_TIERS: PlanId[] = ["pro", "scale", "enterprise"];

export function planLabel(plan: PlanId | null): string {
  return plan ? PLAN_INFO[plan].label : "no plan on file";
}
